<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { recoveryMessage } from './account-recovery.mjs'
import './recovery.css'
const props = defineProps({ client: { type: Object, required: true }, area: { type: String, required: true }, loggedIn: Boolean })
const status = ref(null), password = ref(''), token = ref(''), busy = ref(false), error = ref(''), notice = ref('')
const prefix = `/api/${props.area}/auth/email`
let alive = true, epoch = 0
function clear() { password.value = ''; token.value = ''; status.value = null; notice.value = '' }
onBeforeUnmount(() => { alive = false; epoch++; clear() })
watch(() => props.loggedIn, value => { if (!value) { epoch++; clear() } })
async function load() {
  const current = ++epoch; busy.value = true; error.value = ''
  try { const result = await props.client.request(prefix); if (alive && current === epoch) status.value = result }
  catch (cause) { if (alive && current === epoch) error.value = recoveryMessage(cause) } finally { if (alive && current === epoch) busy.value = false }
}
async function submit(kind) {
  if (busy.value) return
  const current = epoch, body = kind === 'request' ? { password: password.value } : { token: token.value.trim() }
  password.value = ''; token.value = ''; busy.value = true; error.value = ''; notice.value = ''
  try {
    await props.client.request(`${prefix}/${kind}`, { method: 'POST', body })
    if (!alive || current !== epoch) return
    if (kind === 'verify') { await load(); if (alive && props.loggedIn) notice.value = '이메일 소유 확인을 완료했습니다.' }
    else notice.value = '확인 메일을 요청했습니다. 이 화면을 유지한 채 메일의 토큰을 10분 안에 입력해 주세요.'
  } catch (cause) { if (alive && current === epoch) error.value = recoveryMessage(cause) } finally { if (alive && current === epoch) busy.value = false }
}
onMounted(load)
</script>
<template>
  <section class="recovery-ui recovery-card" aria-labelledby="email-proof-heading">
    <div class="recovery-heading"><div><p class="recovery-kicker">ACCOUNT RECOVERY</p><h2 id="email-proof-heading">복구 이메일 확인</h2></div></div>
    <p>현재 이메일의 소유를 확인하면 비밀번호를 잊었을 때 재설정할 수 있습니다. 이메일 변경은 회원 정보에서 처리해 주세요.</p>
    <p v-if="error" class="recovery-error" role="alert">{{ error }}</p><p v-if="notice" class="recovery-notice" role="status">{{ notice }}</p>
    <p v-if="!status && busy">이메일 상태를 확인하고 있습니다.</p>
    <template v-if="status">
      <div class="recovery-email"><strong data-testid="recovery-email">{{ status.emailMasked || '등록된 이메일 없음' }}</strong><span data-testid="email-verified">{{ status.verified ? '소유 확인 완료' : '소유 확인 필요' }}</span></div>
      <p v-if="!status.deliveryAvailable">현재 확인 메일을 사용할 수 없습니다. 운영 담당자에게 문의해 주세요.</p>
      <div v-else-if="status.emailMasked" class="recovery-columns">
        <form @submit.prevent="submit('request')"><label for="email-password">이메일 확인용 현재 비밀번호</label><input id="email-password" v-model="password" type="password" autocomplete="current-password" maxlength="256" :disabled="busy" required><button type="submit" :disabled="busy || !password">확인 메일 요청</button><p class="hint">요청 간격은 1분이며, 계정당 한 시간에 3회까지 요청할 수 있습니다.</p></form>
        <form @submit.prevent="submit('verify')"><label for="email-token">이메일 확인 토큰</label><input id="email-token" v-model="token" type="password" autocomplete="off" maxlength="128" :disabled="busy" required><button type="submit" :disabled="busy || !token.trim()">이메일 확인 완료</button><p class="hint">가장 최근 메일의 토큰을 복사해 붙여 넣으세요. 한 번만 사용할 수 있습니다.</p></form>
      </div><p v-else>회원 정보에 이메일을 등록한 후 다시 확인해 주세요.</p>
    </template>
  </section>
</template>
